'use strict';

/**
 * T315 Module Lock — LD-14 canonical moduleId → backing-blob mapping.
 *
 * Some modules persist data under MORE than one vault blob id. Locking only
 * the "primary" id would leave a parallel blob writable → false security.
 * This map makes a Module Lock apply uniformly to ALL blobs of a module.
 *
 * Known multi-blob modules (audited 2026-06-04, plan LD-14):
 *   - notes  → ['notes', 'notes-host']   (drawer client uses notes-host;
 *                                          Control executor uses notes)
 *   - camera → ['camera-photos', 'files'] (camera writes into filing's
 *                                          shared 'files' blob)
 *
 * Any module not listed maps to itself (identity).
 */

const CANONICAL_TO_BLOBS = Object.freeze({
  notes: ['notes', 'notes-host'],
  camera: ['camera-photos', 'files'],
});

// Reverse index built once. A blob owned by multiple canonical modules
// (e.g. 'files' is owned by both filing and camera) resolves to the FIRST
// canonical owner that is itself the filing/primary owner; we prefer the
// canonical id that equals the blob name when present.
function blobsForModule(moduleId) {
  if (CANONICAL_TO_BLOBS[moduleId]) return CANONICAL_TO_BLOBS[moduleId].slice();
  return [moduleId];
}

function canonicalForBlob(blobId) {
  // If the blob name is itself a canonical module, it is its own canonical id.
  if (CANONICAL_TO_BLOBS[blobId]) return blobId;
  // Otherwise find a canonical module that lists this blob.
  for (const [canonical, blobs] of Object.entries(CANONICAL_TO_BLOBS)) {
    if (blobs.indexOf(blobId) !== -1) {
      // Prefer a canonical whose own name is NOT also a standalone blob the
      // blob equals — but for split cases (notes-host→notes) return canonical.
      if (blobId !== canonical) return canonical;
    }
  }
  return blobId;
}

module.exports = {
  CANONICAL_TO_BLOBS,
  blobsForModule,
  canonicalForBlob,
};
